import {EventFormAnswer, EventFormQuestion} from '../api/event-api.service';

/** Short random id for new questions/options. The server keeps client ids when they are non-blank and unique. */
export function newFormId(): string {
    return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/** Default question pre-filled on new CONFERENCE events; it replaces the old hardcoded Yes/No radio. */
export function defaultAttendingQuestion(): EventFormQuestion {
    return {
        questionId: newFormId(),
        label: 'Are you attending the conference?',
        type: 'SINGLE_SELECT',
        required: true,
        options: [
            {optionId: newFormId(), label: 'Yes'},
            {optionId: newFormId(), label: 'No'}
        ]
    };
}

/**
 * Tidies the organizer's questions before saving: trims text, drops blank option rows (new questions start
 * with two empty ones) and removes questions left completely empty. Returns a message instead when something
 * is half-filled, so the organizer is told what to fix rather than getting a generic save failure.
 */
export function cleanQuestions(questions: EventFormQuestion[] | undefined): {questions: EventFormQuestion[]; error: string | null} {
    const cleaned: EventFormQuestion[] = [];
    for (const q of questions || []) {
        const label = (q.label || '').trim();
        const options = (q.options || [])
            .map(o => ({...o, label: (o.label || '').trim()}))
            .filter(o => o.label.length > 0);
        if (!label && options.length === 0) { continue; } // untouched placeholder
        if (!label) { return {questions: cleaned, error: 'Please give every registration question a label.'}; }
        if (options.length === 0) { return {questions: cleaned, error: `Add at least one option to the question "${label}".`}; }
        const seen = new Set(options.map(o => o.label.toLowerCase()));
        if (seen.size !== options.length) { return {questions: cleaned, error: `The question "${label}" has duplicate options.`}; }
        cleaned.push({...q, label, options});
    }
    return {questions: cleaned, error: null};
}

/** Labels of required questions that have no selection yet. Empty array = ready to submit. */
export function missingRequiredLabels(questions: EventFormQuestion[] | undefined, answers: EventFormAnswer[] | undefined): string[] {
    return (questions || [])
        .filter(q => q.required)
        .filter(q => {
            const a = (answers || []).find(x => x.questionId === q.questionId);
            return !a || !a.selectedOptionIds || a.selectedOptionIds.length === 0;
        })
        .map(q => q.label);
}

/** Drops answers for questions the event no longer defines, so a stale answer can never block a submit. */
export function pruneAnswers(questions: EventFormQuestion[] | undefined, answers: EventFormAnswer[] | undefined): EventFormAnswer[] {
    const ids = new Set((questions || []).map(q => q.questionId));
    return (answers || []).filter(a => ids.has(a.questionId));
}

/** "Meal: Veg · Days: Friday, Sunday" — one line for tables and cards. */
export function formatAnswers(answers: EventFormAnswer[] | undefined): string {
    return (answers || [])
        .filter(a => a.selectedLabels && a.selectedLabels.length > 0)
        .map(a => `${a.questionLabel}: ${a.selectedLabels!.join(', ')}`)
        .join(' · ');
}

/** The selected labels for one question, for per-question CSV columns. */
export function answerLabelsFor(answers: EventFormAnswer[] | undefined, questionId: string): string {
    const a = (answers || []).find(x => x.questionId === questionId);
    return a && a.selectedLabels ? a.selectedLabels.join('; ') : '';
}
